import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { useTimezone } from '../contexts/TimezoneContext';
import { 
  FileText, 
  Plus, 
  Filter, 
  Search, 
  DollarSign,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import Pagination from './Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationsPage = () => {
  const { user } = useContext(AuthContext);
  const { formatDateTime } = useTimezone();
  const [applications, setApplications] = useState([]);
  const { tt } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [managers, setManagers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  // Removed unused states for application creation

  const ITEMS_PER_PAGE = 25;

  const applicationStatuses = [
    { value: 'pending', label: tt('На рассмотрении'), color: 'yellow', icon: Clock },
    { value: 'approved', label: tt('Одобрено'), color: 'green', icon: CheckCircle },
    { value: 'rejected', label: tt('Отклонено'), color: 'red', icon: XCircle },
    { value: 'completed', label: tt('Выполнено'), color: 'blue', icon: CheckCircle }
  ];

  useEffect(() => {
    fetchApplications();
    fetchManagers();
  }, []);

  useEffect(() => {
    // Only refetch if filters are being used
    if (managerFilter || dateFromFilter || dateToFilter) {
      fetchApplications();
    }
  }, [managerFilter, dateFromFilter, dateToFilter]);


  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, managerFilter, dateFromFilter, dateToFilter]);


  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      setManagers(response.data);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      
      // Используем новый endpoint для заявок на вывод средств
      const url = `${API}/withdraw-requests`;
      console.log('🔍 ApplicationsPage: Fetching applications from:', url);
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('✅ ApplicationsPage: Applications fetched:', response.data);
      console.log('📋 ApplicationsPage: First application sample:', response.data[0]);
      
      // Add debug info about data structure
      if (response.data.length > 0) {
        const sampleApp = response.data[0];
        console.log('🔍 ApplicationsPage: Application fields:', Object.keys(sampleApp));
        console.log('👤 ApplicationsPage: Client name field:', sampleApp.client_name);
      }
      
      // Deduplicate applications by ID to prevent duplicates
      const uniqueApplications = response.data.filter((app, index, arr) => 
        arr.findIndex(a => a.id === app.id) === index
      );
      
      console.log('🔧 ApplicationsPage: Deduplication result:', {
        original: response.data.length,
        deduplicated: uniqueApplications.length,
        duplicatesRemoved: response.data.length - uniqueApplications.length
      });
      
      setApplications(uniqueApplications);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCreateApplication = async (e) => {
    e.preventDefault();
    // Функция создания заявки отключена, так как заявки создаются только через торговую платформу
    console.log('Заявки на вывод создаются только через торговую платформу');
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await axios.put(`${API}/withdraw-requests/${requestId}/approve`);
      fetchApplications(); // Refresh the list
      alert(tt('Заявка успешно одобрена'));
    } catch (error) {
      console.error('Error approving request:', error);
      alert(tt('Ошибка при одобрении заявки: ') + (error.response?.data?.detail || error.message));
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axios.put(`${API}/withdraw-requests/${requestId}/reject`);
      fetchApplications(); // Refresh the list
      alert(tt('Заявка отклонена'));
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert(tt('Ошибка при отклонении заявки: ') + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = applicationStatuses.find(s => s.value === status) || { label: status, color: 'gray', icon: AlertCircle };
    const Icon = statusInfo.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
        status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
        status === 'approved' ? 'bg-green-100 text-green-800' :
        status === 'rejected' ? 'bg-red-100 text-red-800' :
        status === 'processing' ? 'bg-blue-100 text-blue-800' :
        'bg-gray-100 text-gray-800'
      }`}>
        <Icon className="w-3 h-3 mr-1" />
        {statusInfo.label}
      </span>
    );
  };

  const filteredApplications = applications.filter(app => {
    try {
      const matchesSearch = searchTerm === '' || 
        (app.client_name && app.client_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (app.client_id && app.client_id.toString().includes(searchTerm)) ||
        (app.amount && app.amount.toString().includes(searchTerm));
      const matchesStatus = statusFilter === '' || app.status === statusFilter;
      const matchesManager = managerFilter === '' || 
        (app.processed_by && String(app.processed_by) === String(managerFilter));
      return matchesSearch && matchesStatus && matchesManager;
    } catch (error) {
      console.error('❌ ApplicationsPage: Error filtering application:', app, error);
      return false;
    }
  });

  // Pagination
  const paginatedApplications = filteredApplications.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  console.log('📊 ApplicationsPage: Applications filtering results:', {
    total: applications.length,
    filtered: filteredApplications.length,
    searchTerm,
    statusFilter,
    managerFilter,
    sampleProcessedBy: applications.length > 0 ? applications[0].processed_by : 'N/A'
  });

  const formatDate = (dateString) => {
    return formatDateTime(dateString);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="page-container">
        {/* Page Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">{tt('Заявки на вывод')}</h1>
            <p className="page-subtitle">{tt('Управление заявками на вывод средств из торговых аккаунтов')}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="card p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={tt('Поиск по ID, клиенту...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
                data-testid="search-applications"
              />
            </div>

            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-select"
                data-testid="filter-status"
              >
                <option value="">{tt('Все статусы')}</option>
                {applicationStatuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={managerFilter}
                onChange={(e) => setManagerFilter(e.target.value)}
                className="form-select"
                data-testid="filter-manager"
              >
                <option value="">{tt('Все менеджеры')}</option>
                {managers.map(manager => (
                  <option key={manager.id} value={manager.id}>
                    {manager.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="form-input"
                placeholder={tt('Дата от')}
                data-testid="filter-date-from"
              />
            </div>

            <div>
              <input
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                className="form-input"
                placeholder={tt('Дата до')}
                data-testid="filter-date-to"
              />
            </div>

            <div>
              <button
                onClick={() => {
                  setSearchTerm(''); 
                  setStatusFilter('');
                  setManagerFilter('');
                  setDateFromFilter('');
                  setDateToFilter('');
                }}
                className="btn btn-secondary w-full"
                data-testid="clear-filters"
              >
                <Filter className="w-4 h-4 mr-2" />
                {tt('Очистить')}
              </button>
            </div>
          </div>
        </div>

        {/* Applications Table - Single View for All Devices */}
        <div className="card overflow-hidden" data-testid="applications-table">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="spinner mr-3"></div>
              <span className="text-gray-600">{tt('Загрузка заявок...')}</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{tt('Клиент')}</th>
                    <th>{tt('Сумма')}</th>
                    <th>{tt('Реквизиты')}</th>
                    <th>{tt('Статус')}</th>
                    <th>{tt('Обработал')}</th>
                    <th>{tt('Дата создания')}</th>
                    <th>{tt('Действия')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedApplications.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>{tt('Заявки не найдены')}</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedApplications.map((application) => (
                      <tr key={application.id} className="hover:bg-gray-50" style={{ minHeight: '60px', height: '60px' }}>
                        <td>
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-2 text-gray-400" />
                            <div>
                              <div className="font-medium text-gray-900">{application.client_name}</div>
                              <div className="text-sm text-gray-500">ID: {application.client_id}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center font-semibold text-gray-900">
                            <DollarSign className="w-4 h-4 mr-1 text-green-600" />
                            ${application.amount.toFixed(2)}
                          </div>
                        </td>
                        <td>
                          <div className="text-sm">
                            {application.withdraw_type === 'account' ? (
                              <>
                                <div className="font-medium">{application.account_holder || '-'}</div>
                                <div className="text-gray-500 font-mono text-xs">
                                  {application.account_number || '-'}
                                </div>
                                <div className="text-blue-500 text-xs">{tt('Банковский счёт')}</div>
                              </>
                            ) : (
                              <>
                                <div className="font-medium">{application.card_holder || '-'}</div>
                                <div className="text-gray-500 font-mono">
                                  {application.card_number ? application.card_number.replace(/(.{4})/g, '$1 ').trim() : '-'}
                                </div>
                                <div className="text-gray-400 text-xs">{tt('Карта')}</div>
                              </>
                            )}
                          </div>
                        </td>
                        <td>{getStatusBadge(application.status)}</td>
                        <td>
                          <div className="flex items-center text-sm text-gray-600">
                            <User className="w-4 h-4 mr-2" />
                            <span className="truncate max-w-24" title={application.processed_by_name}>
                              {application.processed_by_name || '-'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            {formatDate(application.created_at)}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center space-x-2">
                            {application.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApproveRequest(application.id)}
                                  className="btn btn-success btn-sm"
                                  data-testid={`approve-${application.id}`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {tt('Одобрить')}
                                </button>
                                <button
                                  onClick={() => handleRejectRequest(application.id)}
                                  className="btn btn-danger btn-sm"
                                  data-testid={`reject-${application.id}`}
                                >
                                  <XCircle className="w-3 h-3 mr-1" />
                                  {tt('Отклонить')}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile version removed - using only desktop table for all devices */}

        {/* Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="text-2xl font-bold text-blue-600">{filteredApplications.length}</div>
            <div className="text-sm text-gray-600">{tt('Всего заявок')}</div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-yellow-600">
              {filteredApplications.filter(a => a.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-600">{tt('Ожидают')}</div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-green-600">
              {filteredApplications.filter(a => a.status === 'approved').length}
            </div>
            <div className="text-sm text-gray-600">{tt('Одобрены')}</div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(filteredApplications.reduce((sum, a) => sum + a.amount, 0))}
            </div>
            <div className="text-sm text-gray-600">{tt('Общая сумма')}</div>
          </div>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalItems={filteredApplications.length}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
    </div>
  );
};

export default ApplicationsPage;